import { addHours, differenceInCalendarDays, differenceInHours, isBefore, isValid, subMinutes } from "date-fns";
import { fromZonedTime, getTimezoneOffset, toZonedTime } from "date-fns-tz";
import { formatRelative } from "./formatRelative";

/**
 * The timezone of the browser.
 * This only works in the browser, not server-side.
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/resolvedOptions
 * @example "Europe/Berlin"
 */
export const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const ensureTimezone = (timezone: string | undefined | null): string => timezone || browserTimezone;
/**
 * Returns the timezone name in the short format, in US English whatever the browser's locale, e.g. "EST".
 * @param timezone IANA timezone name, e.g. "America/New_York"
 * @param at the moment to name it for: "EDT" or "EST" depends on the date. Defaults to now.
 * @returns the timezone name in the short format, e.g. "EST"; a zone with no US name reads its offset, e.g. "GMT+1"
 */
export const shortenTimezone = (timezone: string | undefined | null, at: Date = new Date()): string => {
  const tz = ensureTimezone(timezone);
  return (
    new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" })
      .formatToParts(at)
      .find((part) => part.type === "timeZoneName")?.value ?? tz
  );
};

/**
 * Converts a date to UTC, assuming the date is in the given timezone.
 * @param date in any timezone
 * @param timezone the date is supposed to be in
 * @returns the date in UTC
 */
export const convertToUtc = (date: Date | string, timezone: string | null | undefined): Date =>
  fromZonedTime(date, ensureTimezone(timezone));

/**
 * Applies the UTC offset so the date displays as if it's UTC but with the current timezone intact.
 * @param date in any timezone
 * @param timezone the date is supposed to be in
 * @returns the date as if it were UTC
 */
export const jumpToUtc = (date: Date | string, timezone: string): Date => {
  const d = new Date(date);
  const utcOffset = getTimezoneOffset(ensureTimezone(timezone), d) / (60 * 60 * 1000);
  // We need to apply the opposite of the offset to get the date as if it were UTC, hence the * -1.
  return addHours(d, utcOffset * -1);
};

/**
 * Converts a date in UTC to the given timezone.
 * @param date in UTC
 * @param timezone the date should be converted to
 * @returns the date in the given timezone
 */
export const convertToTimezone = (date: Date | string, timezone: string | null | undefined): Date =>
  toZonedTime(date, ensureTimezone(timezone));

/**
 * Nullable-passthrough variant of {@linkcode convertToTimezone}. Returns `null`/`undefined`
 * unchanged so callers can map over optional date fields without conditional wrappers.
 */
export function convertToTimezoneNullable(date: Date | string, timezone: string): Date;
export function convertToTimezoneNullable(date: Date | string | null, timezone: string): Date | null;
export function convertToTimezoneNullable(date: Date | string | undefined, timezone: string): Date | undefined;
export function convertToTimezoneNullable(
  date: Date | string | null | undefined,
  timezone: string,
): Date | null | undefined;
export function convertToTimezoneNullable(
  date: Date | string | null | undefined,
  timezone: string,
): Date | null | undefined {
  return date == null ? date : convertToTimezone(date, timezone);
}

/**
 * Converts a date from one timezone to another.
 * @param date a date in {@linkcode fromTimezone}
 * @param fromTimezone the timezone of the date
 * @param toTimezone the timezone the date should be converted to
 * @returns the date in the {@linkcode toTimezone}
 */
export const convertBetweenTimezones = (date: Date | string, fromTimezone: string, toTimezone: string): Date =>
  convertToTimezone(convertToUtc(date, fromTimezone), toTimezone);

/**
 * Returns the current date in the given timezone.
 * @param timezone the timezone the date should be in. Defaults to browser timezone.
 * @returns the current date in the given timezone
 * @example nowInTimezone("Europe/Berlin")
 */
export const nowInTimezone = (timezone: string | null | undefined): Date =>
  convertBetweenTimezones(new Date(), browserTimezone, timezone ?? browserTimezone);

/**
 * Is the given date in the past?
 * @param date the date to check
 * @param timezone the timezone the date is in. Defaults to browser timezone.
 * @returns true if the date is in the past
 */
export const isPast = (date: Date | number, timezone: string | null | undefined): boolean =>
  isBefore(date, nowInTimezone(timezone));

/**
 * Formats the distance from the given date to now.
 *
 * Measures real elapsed time. `date` is a "zoned" date (the location's wall-clock time stored as a browser-local Date,
 * see {@linkcode convertToTimezone}), so it's converted back to the true instant before comparing with the real now.
 * Subtracting two zoned dates instead gives the wall-clock gap, which is an hour off across a daylight-saving change.
 * @param date the date to format the distance to, zoned for {@linkcode timezone}. A raw date works with no timezone.
 * @param timezone the timezone the date is in. Defaults to browser timezone.
 * @returns the formatted distance assuming the date is in the given timezone
 */
export const formatRelativeToNow = (date: Date | number, timezone: string | null | undefined): string => {
  if (!isValid(date)) return "-";
  return formatRelative(convertToUtc(new Date(date), timezone), new Date(), { numeric: "always" });
};

/**
 * Is the given date at most {@linkcode hours} real hours away from now? Dates in the past always are.
 * @param date zoned for {@linkcode timezone}, see {@linkcode formatRelativeToNow}
 */
export const isWithinHours = (date: Date | number, hours: number, timezone: string | null | undefined) =>
  differenceInHours(convertToUtc(new Date(date), timezone), new Date()) <= hours;

export const isWithin15Minutes = (date: Date, timezone: string | null) => isPast(subMinutes(date, 15), timezone);

export const getDateTimezoneOffsetInDays = (date: Date, timezone: string) =>
  differenceInCalendarDays(jumpToUtc(date, timezone), date);
